import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as lpsApi from './lps.api';

import type { LpsConnectionInput, LpsDeliveryListQuery, LpsLeadListQuery } from './lps.api';

import { extractServerError } from '@/lib/api-error';

const QK = {
  all: ['lps'] as const,
  leads: (query: LpsLeadListQuery) => ['lps', 'leads', query] as const,
  lead: (id: string) => ['lps', 'lead', id] as const,
  overview: ['lps', 'overview'] as const,
  connectors: ['lps', 'connectors'] as const,
  connections: ['lps', 'connections'] as const,
  agents: (id: string) => ['lps', 'lead', id, 'agents'] as const,
};

const errorText = (err: unknown, fallback: string): string =>
  extractServerError(err).message ?? fallback;

export const useLpsLeadsList = (query: LpsLeadListQuery) =>
  useQuery({
    queryKey: QK.leads(query),
    queryFn: () => lpsApi.listLpsLeads(query),
    staleTime: 15_000,
  });

export const useLpsLead = (id: string | null) =>
  useQuery({
    queryKey: QK.lead(id ?? ''),
    queryFn: () => lpsApi.getLpsLead(id!),
    enabled: Boolean(id),
  });

/** `path` lets the Marketing Report read the same summary under its own endpoint
 *  (and permission); it defaults to the LPS endpoint and keys the cache per path. */
export const useLpsDestinationSummary = (query: lpsApi.LpsSummaryQuery, path?: string) =>
  useQuery({
    queryKey: ['lps', 'by-destination', path ?? 'lps', query] as const,
    queryFn: () => lpsApi.getLpsDestinationSummary(query, path),
    staleTime: 30_000,
  });

export const useLpsChannelSummary = (query: lpsApi.LpsSummaryQuery, path?: string) =>
  useQuery({
    queryKey: ['lps', 'by-channel', path ?? 'lps', query] as const,
    queryFn: () => lpsApi.getLpsChannelSummary(query, path),
    staleTime: 30_000,
  });

export const useLpsOverview = () =>
  useQuery({ queryKey: QK.overview, queryFn: lpsApi.getLpsOverview, staleTime: 15_000 });

export const useLpsLeadAgents = (id: string | null) =>
  useQuery({
    queryKey: QK.agents(id ?? ''),
    queryFn: () => lpsApi.getLpsLeadAgents(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
  });

export const useLpsDeliveries = (query: LpsDeliveryListQuery) =>
  useQuery({
    queryKey: ['lps', 'deliveries', query],
    queryFn: () => lpsApi.listLpsDeliveries(query),
    staleTime: 15_000,
  });

export const useLpsDelivery = (id: string | null) =>
  useQuery({
    queryKey: ['lps', 'delivery', id ?? ''],
    queryFn: () => lpsApi.getLpsDelivery(id!),
    enabled: Boolean(id),
  });

export const useLpsConnectors = () =>
  useQuery({
    queryKey: QK.connectors,
    queryFn: lpsApi.listLpsConnectors,
    staleTime: 5 * 60_000,
    retry: false,
  });

/** Admin-only on the API (webhook URLs carry the secret); pass `enabled` false for others. */
export const useLpsConnections = (enabled = true) =>
  useQuery({
    queryKey: QK.connections,
    queryFn: lpsApi.listLpsConnections,
    staleTime: 15_000,
    retry: false,
    enabled,
  });

/** Anything that changes LPS invalidates the whole `lps` cache - lists are small. */
const useLpsMutation = <TVars, TResult>(
  fn: (vars: TVars) => Promise<TResult>,
  messages: { success: string | ((result: TResult) => string); failure: string },
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: QK.all });
      toast.success(
        typeof messages.success === 'function' ? messages.success(result) : messages.success,
      );
    },
    onError: (err: unknown) => toast.error(errorText(err, messages.failure)),
  });
};

export const useRetryLpsLead = () =>
  useLpsMutation((id: string) => lpsApi.retryLpsLead(id), {
    success: 'Sent to the CRM again',
    failure: 'Could not retry this lead',
  });

/** The toast reflects what the CRM did: created and assigned, or still waiting. */
export const useAssignLpsLead = () =>
  useLpsMutation(
    ({ id, userId }: { id: string; userId: string | null }) => lpsApi.assignLpsLead(id, userId),
    {
      success: (forward) =>
        forward?.status === 'FORWARDED'
          ? `Assigned and sent to the CRM as ${forward.crmLeadReference}`
          : forward?.status === 'AWAITING_ASSIGNMENT'
            ? `Still waiting: ${forward.error ?? 'no agent could be picked'}`
            : 'Queued for the CRM',
      failure: 'Could not assign this lead',
    },
  );

/** Toast says how the batch landed; the dialog shows the failures. */
export const useBulkAssignLpsLeads = () =>
  useLpsMutation(
    ({
      ids,
      userId,
      onProgress,
    }: {
      ids: ReadonlyArray<string>;
      userId: string | null;
      onProgress?: (done: number, total: number) => void;
    }) => lpsApi.bulkAssignLpsLeads(ids, userId, onProgress),
    {
      success: (r) =>
        [
          `${r.assigned} lead${r.assigned === 1 ? '' : 's'} assigned and sent to the CRM`,
          r.waiting > 0 ? `${r.waiting} still waiting for an agent` : null,
          r.failed.length > 0 ? `${r.failed.length} failed` : null,
        ]
          .filter(Boolean)
          .join(', '),
      failure: 'Could not assign these leads',
    },
  );

export const useCreateLpsConnection = () =>
  useLpsMutation((input: LpsConnectionInput) => lpsApi.createLpsConnection(input), {
    success: (c) => `Connection "${c.name}" created`,
    failure: 'Could not create the connection',
  });

export const useUpdateLpsConnection = () =>
  useLpsMutation(
    ({ id, input }: { id: string; input: LpsConnectionInput }) =>
      lpsApi.updateLpsConnection(id, input),
    {
      success: (c) => `Connection "${c.name}" saved`,
      failure: 'Could not save the connection',
    },
  );

export const useRotateLpsSecret = () =>
  useLpsMutation((id: string) => lpsApi.rotateLpsConnectionSecret(id), {
    success: 'New webhook URL issued - update it in the provider',
    failure: 'Could not rotate the secret',
  });

export const useDeleteLpsConnection = () =>
  useLpsMutation((id: string) => lpsApi.deleteLpsConnection(id), {
    success: 'Connection deleted - its leads are kept',
    failure: 'Could not delete the connection',
  });

export const lpsErrorText = errorText;
